import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import { useData } from 'src/contexts/DataContext';
import {
  StyledCard,
  Commitment,
  Description,
  Link,
  PlanNote,
  Price,
  SelectButton,
  Title,
  StyledImage,
  PrinterDivider,
  PagesPerMonth,
  CurrentPlanInfo,
  PrinterCardLabel
} from './styles';
import SamePrinterModal from '../../SamePrinterModal';
import { PrinterItem } from 'src/utils/Products/productFilter';
import { IconInfo } from '@veneer/core';
import { primitives } from '@veneer/primitives';
import { useTranslationWithInterpolation } from 'src/hooks/useTranslationWithValues/useTranslationWithValuesHelper';
import {
  calculatePriceDifference,
  formatPrice
} from 'src/utils/Products/getCalculatePriceDifferance';
import AutomaticFirmwareUpdatesModal from '../../AutomaticFirmwareUpdatesModal';

interface PrinterDetailsProps {
  nonCurrentPrinterDetails?: PrinterItem;
  currentPrinterDetails?: PrinterItem[];
  selectionState?: {
    selected: boolean;
    isCurrent: boolean;
    onSelect: () => void;
  };
}

const PrinterCard: React.FC<PrinterDetailsProps> = ({
  nonCurrentPrinterDetails,
  selectionState,
  currentPrinterDetails
}) => {
  const dataContext = useData();
  const { selectedPrinterSku } = dataContext || {};
  const { getText, getTextWithValues } = useTranslationWithInterpolation();
  const { selected } = selectionState;
  const [showSamePrinterModal, setShowSamePrinterModal] = useState(false);
  const [showFirmwareModal, setShowFirmwareModal] = useState(false);

  const htmlString = nonCurrentPrinterDetails?.shortDescription?.html || '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const showDescriptionText = doc.body.textContent || '';

  const recommendedPlan = nonCurrentPrinterDetails?.inkPlan.find(
    (plan) => plan.recommendedPlan === true
  );
  const { increaseDecreaseAmount, increaseOrDecrease } =
    calculatePriceDifference(
      recommendedPlan?.totalPrice,
      currentPrinterDetails[0]?.inkPlan[0].totalPrice
    );
  const currentNames = currentPrinterDetails.map((p) => p.printerName);

  const planType = currentNames.includes(nonCurrentPrinterDetails?.printerName);
  let planNote = '';
  if (planType) {
    planNote = getText(
      'printerDetails.currentPlanInfo',
      'This is your current print plan.'
    );
  } else {
    planNote = getTextWithValues(
      'printerDetails.currentPlan',
      'Your current plan is {{printerModel}} printer with {{pages}} pages/month.<br/><b> Plan fee will {{difference}} by only {{charges}}/month</b>',
      {
        printerModel: currentPrinterDetails[0]?.printerName,
        pages: currentPrinterDetails[0]?.inkPlan[0]?.pagesPerMonth,
        difference: increaseOrDecrease,
        charges: `$${increaseDecreaseAmount}`
      }
    );
  }
  const sanitizedPlanNote = DOMPurify.sanitize(planNote);

  const handleSelectClick = () => {
    if (planType) {
      setShowSamePrinterModal(true);
    } else {
      setShowFirmwareModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowSamePrinterModal(false);
    setShowFirmwareModal(false);
  };

  const handleProceed = () => {
    setShowSamePrinterModal(false);
    setShowFirmwareModal(true);
  };

  const handleFirmwareProceed = () => {
    setShowFirmwareModal(false);
    if (selectedPrinterSku) {
      selectedPrinterSku(nonCurrentPrinterDetails?.productSKU);
    }
  };
  return (
    <StyledCard
      selected={selected}
      data-testid={`printer-card-${nonCurrentPrinterDetails?.printerName}`}
      data-selected={selected}
    >
      <PrinterCardLabel>
        {nonCurrentPrinterDetails?.modelPurpose}
      </PrinterCardLabel>
      <Title>{nonCurrentPrinterDetails?.printerName}</Title>
      <StyledImage
        src={nonCurrentPrinterDetails?.thumbnailImage}
        alt={nonCurrentPrinterDetails?.printerName}
      />

      <Description>{showDescriptionText}</Description>
      <Link
        href={nonCurrentPrinterDetails?.fullspecification}
        target="_blank"
      >
        {'See all specs'}
      </Link>
      <PrinterDivider />
      <Commitment>
        {getTextWithValues(
          'printerDetails.commitmentDetails',
          '{{years}} years commitment',
          {
            years: recommendedPlan?.commitmentPeriod
          }
        )}
      </Commitment>
      <Price>
        {getTextWithValues('printerCards.totalPerMonth', '{{amount}}/month', {
          amount: formatPrice(Math.abs(recommendedPlan?.totalPrice))
        })}
      </Price>
      <PagesPerMonth>
        <div>
          {getTextWithValues(
            'printerDetails.includesPages',
            'Includes {{pages}} pages/month',
            {
              pages: recommendedPlan?.pagesPerMonth
            }
          )}
          {nonCurrentPrinterDetails?.isPaperActive && (
            <IconInfo
              size={20}
              color="#636363"
            />
          )}
        </div>
      </PagesPerMonth>
      <PlanNote
        data-testid={`plan-note-${nonCurrentPrinterDetails?.printerName}`}
      >
        {planType ? (
          <CurrentPlanInfo>
            <IconInfo
              filled
              color={primitives.color.electricBlue6}
              height={16}
            />
            <b dangerouslySetInnerHTML={{ __html: sanitizedPlanNote }}></b>
          </CurrentPlanInfo>
        ) : (
          <div dangerouslySetInnerHTML={{ __html: sanitizedPlanNote }}></div>
        )}
      </PlanNote>
      <SelectButton
        appearance="secondary"
        onClick={handleSelectClick}
        aria-pressed={selected}
        data-testid={`select-btn-${nonCurrentPrinterDetails?.printerName}`}
      >
        Select this printer
      </SelectButton>

      <SamePrinterModal
        show={showSamePrinterModal}
        onClose={handleCloseModal}
        onProceed={handleProceed}
      />
      {/* Automatic Firmware Updates Modal */}
      <AutomaticFirmwareUpdatesModal
        show={showFirmwareModal}
        onClose={handleCloseModal}
        onProceed={handleFirmwareProceed}
      />
    </StyledCard>
  );
};

export default PrinterCard;
